/**
 * House and Sign Lordship Utilities
 * 
 * Centralized lord calculation functions to avoid code duplication.
 * Used by chatContext, yogaDetector, houseScores, etc.
 */

import type { PlanetName, HouseNum } from '../astro/feeds/types';

/**
 * Sign lords (1-indexed: 1=Aries, 2=Taurus, etc.)
 * This is the SINGLE SOURCE OF TRUTH for sign lordships.
 */
export const SIGN_LORDS: Record<number, PlanetName> = {
  1: 'Mars',      // Aries
  2: 'Venus',     // Taurus
  3: 'Mercury',   // Gemini
  4: 'Moon',      // Cancer
  5: 'Sun',       // Leo
  6: 'Mercury',   // Virgo
  7: 'Venus',     // Libra
  8: 'Mars',      // Scorpio
  9: 'Jupiter',   // Sagittarius
  10: 'Saturn',   // Capricorn
  11: 'Saturn',   // Aquarius
  12: 'Jupiter'   // Pisces
};

/**
 * Get the sign that falls in a given house
 * @param house - House number (1-12)
 * @param ascendantSign - Ascendant sign (1-12, where 1=Aries)
 * @returns Sign number (1-12)
 */
export function getHouseSign(house: HouseNum, ascendantSign: number): number {
  // House 1 = ascendant sign, House 2 = next sign, etc.
  // Formula: (ascendantSign + house - 2) mod 12, then +1 for 1-indexing
  return ((ascendantSign + house - 2) % 12) + 1;
}

/**
 * Get the lord of a house
 * @param house - House number (1-12)
 * @param ascendantSign - Ascendant sign (1-12, where 1=Aries)
 * @returns Planet that rules the sign in this house
 */
export function getHouseLord(house: HouseNum, ascendantSign: number): PlanetName {
  const houseSign = getHouseSign(house, ascendantSign);
  return SIGN_LORDS[houseSign];
}

/**
 * Get all houses ruled by a planet
 * @param planet - Planet name
 * @param ascendantSign - Ascendant sign (1-12, where 1=Aries)
 * @returns Array of house numbers (1-12) ruled by this planet
 */
export function getHousesRuledByPlanet(planet: PlanetName, ascendantSign: number): HouseNum[] {
  const houses: HouseNum[] = [];
  
  for (let sign = 1; sign <= 12; sign++) {
    if (SIGN_LORDS[sign] === planet) {
      // Convert sign to house relative to ascendant
      let house = sign - ascendantSign + 1;
      if (house <= 0) house += 12;
      houses.push(house as HouseNum);
    }
  }
  
  return houses;
}

/**
 * Get the sign lord (dispositor) of a sign
 * @param sign - Sign number (1-12, where 1=Aries)
 * @returns Planet that rules this sign
 */
export function getSignLord(sign: number): PlanetName {
  return SIGN_LORDS[sign];
}

/**
 * Check if a planet is the lord of a specific house
 * @param planet - Planet name
 * @param house - House number (1-12)
 * @param ascendantSign - Ascendant sign (1-12)
 * @returns true if planet is the lord of this house
 */
export function isPlanetLordOfHouse(planet: PlanetName, house: HouseNum, ascendantSign: number): boolean {
  return getHouseLord(house, ascendantSign) === planet;
}

/**
 * Get own signs for a planet (natural zodiac)
 * @param planet - Planet name (excluding Rahu/Ketu)
 * @returns Array of sign numbers where planet has rulership
 */
export function getOwnSigns(planet: Exclude<PlanetName, 'Rahu' | 'Ketu'>): number[] {
  const ownSigns: number[] = [];
  
  for (let sign = 1; sign <= 12; sign++) {
    if (SIGN_LORDS[sign] === planet) {
      ownSigns.push(sign);
    }
  }
  
  return ownSigns;
}

// ===== DUSTHANA LORD PENALTY CALCULATION =====

/** Parashari aspect offsets (1-based absolute targets relative to source house) */
const DUSTHANA_ASPECT_OFFSETS: Record<string, number[]> = {
  Sun: [7], Moon: [7], Mercury: [7], Venus: [7],
  Mars: [4, 7, 8],
  Jupiter: [5, 7, 9],
  Saturn: [3, 7, 10],
  Rahu: [5, 7, 9],
  Ketu: [5, 7, 9],
};

/** Natural malefic planets */
const NATURAL_MALEFICS = new Set<string>(['Sun', 'Mars', 'Saturn', 'Rahu', 'Ketu']);

/**
 * 0-based offsets from a house to its 6th, 8th, and 12th positions.
 * (lordHouse - ownHouse + 12) % 12
 *   5 = 6th from own house
 *   7 = 8th from own house
 *  11 = 12th from own house
 */
const DUSTHANA_OFFSETS_0BASED = new Set([5, 7, 11]);

export interface LordDusthanaPenalty {
  penalty: number;
  lord: string;
  lordHouse: number;
  severity: 'mild' | 'moderate' | 'severe' | 'critical';
  reason: string;
  /** True when Jupiter aspects the owned house — score/chip remain but rating override is cancelled */
  jupiterProtected?: boolean;
}

/**
 * Compute lord dusthana penalties for all 12 houses.
 *
 * A penalty fires when a house lord is placed in the 6th, 8th, or 12th
 * house FROM the house it rules (dusthana from own house).
 *
 * Tiers:
 *   -10  Dusthana only
 *   -18  Dusthana + malefic aspect received by lord's placed house
 *   -25  Dusthana + malefic conjunction with lord (same house)
 *   -30  Any of above + the owned house itself is empty AND unaspected (critical)
 *
 * @param ascendantSignIndex - Ascendant sign 1-indexed (1=Aries … 12=Pisces)
 * @param planetPlacements   - Map of planet name → { house: 1–12 }
 * @returns Record keyed by house number (only houses with a dusthana lord)
 */
export function computeLordDusthanaPenalties(
  ascendantSignIndex: number,
  planetPlacements: Record<string, { house: number }>
): Record<number, LordDusthanaPenalty> {
  const result: Record<number, LordDusthanaPenalty> = {};

  /**
   * Helper: returns the houses that a planet aspects (1-based absolute house numbers)
   * using standard Parashari rules.
   */
  function getAspectedHouses(planet: string, fromHouse: number): number[] {
    const offsets = DUSTHANA_ASPECT_OFFSETS[planet] || [7];
    return offsets.map(off => {
      let target = fromHouse + off - 1;
      if (target > 12) target -= 12;
      return target;
    });
  }

  for (let h = 1; h <= 12; h++) {
    const lord = getHouseLord(h as HouseNum, ascendantSignIndex);
    const lordData = planetPlacements[lord];
    if (!lordData) continue;

    const lordHouse = lordData.house;
    const offset0 = (lordHouse - h + 12) % 12;
    if (!DUSTHANA_OFFSETS_0BASED.has(offset0)) continue;

    const dusthanaNth = offset0 === 5 ? '6th' : offset0 === 7 ? '8th' : '12th';

    // --- Jupiter aspect check: if Jupiter aspects owned house h, rating override is nullified ---
    // (score penalty and chip still apply — only the 'Needs Attention' status is cancelled)
    const jupiterData = planetPlacements['Jupiter'];
    const jupiterProtected = jupiterData
      ? getAspectedHouses('Jupiter', jupiterData.house).includes(h)
      : false;

    // --- Malefic conjunction: natural malefic in the same house as the lord ---
    const maleficConjuncts = Object.entries(planetPlacements)
      .filter(([p, pl]) => p !== lord && NATURAL_MALEFICS.has(p) && pl.house === lordHouse)
      .map(([p]) => p);

    // --- Malefic aspect to lord's house (from a different house) ---
    const maleficAspectors = Object.entries(planetPlacements)
      .filter(([p, pl]) => {
        if (!NATURAL_MALEFICS.has(p)) return false;
        if (pl.house === lordHouse) return false; // conjunction, not aspect
        return getAspectedHouses(p, pl.house).includes(lordHouse);
      })
      .map(([p]) => p);

    // --- Is the owned house h itself empty AND unaspected? ---
    const occupants = Object.entries(planetPlacements)
      .filter(([, pl]) => pl.house === h)
      .map(([p]) => p);

    const isHouseAspected = Object.entries(planetPlacements).some(([planet, pl]) =>
      getAspectedHouses(planet, pl.house).includes(h)
    );

    const isEmptyUnaspected = occupants.length === 0 && !isHouseAspected;

    // --- Assign penalty tier ---
    let penalty: number;
    let severity: LordDusthanaPenalty['severity'];
    let reason: string;

    if (maleficConjuncts.length > 0) {
      penalty = -25;
      severity = 'severe';
      reason = `${lord} (lord of H${h}) in H${lordHouse} — ${dusthanaNth} from own house, conjunct ${maleficConjuncts.join('/')}`;
    } else if (maleficAspectors.length > 0) {
      penalty = -18;
      severity = 'moderate';
      reason = `${lord} (lord of H${h}) in H${lordHouse} — ${dusthanaNth} from own house, aspected by ${maleficAspectors.join('/')}`;
    } else {
      penalty = -10;
      severity = 'mild';
      reason = `${lord} (lord of H${h}) in H${lordHouse} — ${dusthanaNth} from own house`;
    }

    // Hard cap at -30 when owned house is empty and unaspected
    if (isEmptyUnaspected) {
      penalty = -30;
      severity = 'critical';
      reason += `; H${h} is empty & unaspected (entirely dependent on afflicted lord)`;
    }

    result[h] = { penalty, lord, lordHouse, severity, reason, jupiterProtected: jupiterProtected || undefined };
  }

  return result;
}
